/**
 * Run Detail Report — print layout (#783 Slice 2).
 *
 * Same shape and discipline as `VariantQualityLayout`: presentation
 * only, renders solely from the `RunDetailViewModel` fed to it; never
 * queries Mongo, calls a domain service, or makes another API call.
 * Adding this layout is the test of whether Slice 1's platform is
 * generic — and the answer is: a new layout + a dispatcher case in
 * `PrintSnapshotPage`, no other frontend platform changes.
 */
import { cn } from '@/lib/utils'
import { fmtDateTime, fmtDuration } from '../format'
import type {
  RdRegressionSummary,
  RunDetailViewModel,
} from '../types'

interface RunDetailLayoutProps {
  viewModel: RunDetailViewModel
}

export function RunDetailLayout({ viewModel }: RunDetailLayoutProps) {
  const { context, summary, regression, failures, definition } = viewModel
  // Defense-in-depth: see the matching guard in
  // `components/detailedTestResults/ViewRoot.tsx` for the rationale.
  // Mongoose's Mixed-type snapshot round-trip drops empty objects
  // somewhere; without this guard a missing `tags` field crashes the
  // whole layout → blank PDF.
  const tagEntries = Object.entries(context.tags ?? {})
  return (
    <div className="bg-white text-gray-900 mx-auto max-w-[8.5in] p-8 font-sans text-sm leading-snug">
      {/* Header */}
      <header className="border-b border-gray-300 pb-4">
        {/* "OttoTester ·" prefix dropped in #841 — brand identity is
            already established by the per-page PDF header (logo +
            wordmark) AND the footer; a third instance on the body
            eyebrow was redundant. */}
        <div className="text-[10px] tracking-widest text-gray-500">
          Run Detail Report
        </div>
        <h1 className="mt-1 text-2xl font-bold">{context.variantName}</h1>
        <div className="mt-0.5 text-sm text-gray-600">
          {context.appName} ·{' '}
          {context.suiteName ? `Suite: ${context.suiteName}` : 'Ad-hoc run'}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1">
            <span className="opacity-70">Run</span>
            <code className="font-mono text-gray-700">{context.runId}</code>
          </span>
          <span>·</span>
          <RunTypeBadge runType={context.runType} />
          <span>·</span>
          <StatusBadge status={context.status} />
          <span>·</span>
          <span>Started {fmtDateTime(context.startedAt)}</span>
          <span>·</span>
          <span>Duration {fmtDuration(context.durationMs)}</span>
        </div>
        {tagEntries.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span className="text-gray-500">Tags:</span>
            {tagEntries.map(([k, v]) => (
              <span
                key={k}
                className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-700"
              >
                {k}={v}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* Summary */}
      <section className="mt-5 grid grid-cols-4 gap-3">
        <SummaryTile label="Total" value={summary.total} />
        <SummaryTile label="Passed" value={summary.passed} tone="success" />
        <SummaryTile label="Failed" value={summary.failed} tone="destructive" />
        <SummaryTile label="Skipped" value={summary.skipped} tone="muted" />
      </section>

      {/* Regression vs prior */}
      <section className="mt-5">
        <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
          Regression vs prior run
        </div>
        <div className="mt-1.5">
          <RegressionSection
            runType={context.runType}
            regression={regression}
          />
        </div>
      </section>

      {/* Failure breakdown */}
      <section className="mt-5">
        <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
          Failure breakdown ({failures.total})
        </div>
        {failures.total === 0 ? (
          <div className="mt-1.5 rounded border border-dashed border-gray-300 p-4 text-center text-sm text-gray-600">
            No failures in this run.
          </div>
        ) : (
          <div className="mt-1.5 rounded border border-gray-200 bg-gray-50 p-3">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <DefectFigure
                count={failures.byDefectType.product}
                label={
                  failures.byDefectType.product === 1
                    ? 'product defect'
                    : 'product defects'
                }
                tone="destructive"
              />
              <DefectFigure
                count={failures.byDefectType.broken}
                label="broken"
                tone="warning"
              />
              {failures.byDefectType.uncategorized > 0 && (
                <DefectFigure
                  count={failures.byDefectType.uncategorized}
                  label="uncategorized"
                  tone="muted"
                />
              )}
            </div>
            {failures.byCategory.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600">
                <span className="text-gray-500">By category:</span>
                {failures.byCategory.map((c) => (
                  <span key={c.errorType}>
                    <span className="font-mono">{c.errorType}</span>{' '}
                    <span className="tabular-nums">{c.count}</span>
                  </span>
                ))}
              </div>
            )}
            <p className="mt-2 text-[11px] text-gray-500">
              Product defects are real app failures an assertion caught;
              broken tests couldn't complete (selector, timeout, navigation,
              network) — usually a test-maintenance signal, not an app bug.
            </p>
          </div>
        )}
      </section>

      {/* Captured issues (#1515 Slice 4) — console/network events logged
          during the run. Absent section when nothing was captured. */}
      {viewModel.issues && (
        <section className="mt-5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Captured issues ({viewModel.issues.testCount}{' '}
            {viewModel.issues.testCount === 1 ? 'test' : 'tests'})
          </div>
          <div className="mt-1.5 rounded border border-gray-200 bg-gray-50 p-3">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
              {viewModel.issues.consoleCount > 0 && (
                <span>
                  <span className="font-mono font-semibold text-amber-700">
                    {viewModel.issues.consoleCount}
                  </span>{' '}
                  console{' '}
                  {viewModel.issues.consoleCount === 1 ? 'event' : 'events'}
                </span>
              )}
              {viewModel.issues.networkCount > 0 && (
                <span>
                  <span className="font-mono font-semibold text-violet-700">
                    {viewModel.issues.networkCount}
                  </span>{' '}
                  network{' '}
                  {viewModel.issues.networkCount === 1 ? 'event' : 'events'}
                </span>
              )}
            </div>
            <table className="mt-2 w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-300 text-left text-gray-600">
                  <th className="py-1.5 pr-2 font-medium">Test</th>
                  <th className="w-24 py-1.5 pr-2 font-medium">Result</th>
                  <th className="py-1.5 font-medium">Issues</th>
                </tr>
              </thead>
              <tbody>
                {viewModel.issues.tests.map((t, i) => (
                  <tr
                    key={`${t.testName}-${i}`}
                    className="border-b border-gray-100 align-top"
                  >
                    <td className="py-1.5 pr-2 font-mono">{t.testName}</td>
                    <td
                      className={cn(
                        'py-1.5 pr-2 font-medium',
                        issueResultTone(t.status),
                      )}
                    >
                      {issueResultLabel(t.status)}
                    </td>
                    <td className="py-1.5 text-gray-700">
                      {t.consoleCount > 0 && <span>{t.consoleCount} console</span>}
                      {t.consoleCount > 0 && t.networkCount > 0 && ' · '}
                      {t.networkCount > 0 && <span>{t.networkCount} network</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {viewModel.issues.more > 0 && (
              <div className="mt-1.5 text-[11px] text-gray-500">
                …and {viewModel.issues.more} more{' '}
                {viewModel.issues.more === 1 ? 'test' : 'tests'} with issues —
                see the detailed report or open the run in OttoTester.
              </div>
            )}
            <p className="mt-2 text-[11px] text-gray-500">
              Console errors/warnings and network 4xx/5xx responses logged
              during each test. A passing test can still log issues; full
              per-event detail is in the detailed report.
            </p>
          </div>
        </section>
      )}

      {/* Top failing tests */}
      {failures.topFailures.length > 0 && (
        <section className="mt-5">
          <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Top failing tests ({failures.topFailures.length}
            {failures.total > failures.topFailures.length
              ? ` of ${failures.total}`
              : ''}
            )
          </div>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-gray-300 text-left text-gray-600">
                <th className="py-1.5 pr-2 font-medium">Test</th>
                <th className="w-36 py-1.5 pr-2 font-medium">Error type</th>
                <th className="py-1.5 font-medium">Error preview</th>
              </tr>
            </thead>
            <tbody>
              {failures.topFailures.map((t, i) => (
                <tr
                  key={`${t.testName}-${i}`}
                  className="border-b border-gray-100 align-top"
                >
                  <td className="py-1.5 pr-2 font-mono">{t.testName}</td>
                  <td className="py-1.5 pr-2 font-mono text-gray-700">
                    {t.errorType}
                  </td>
                  <td className="py-1.5 text-gray-700">{t.errorPreview}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Footer */}
      <footer className="mt-8 border-t border-gray-300 pt-3 text-[10px] text-gray-500">
        <div>
          Generated by OttoTester · {definition.id} v{definition.version} ·{' '}
          {fmtDateTime(viewModel.generatedAt)}
        </div>
        <div className="mt-1">
          Live view &amp; detail:{' '}
          <a className="underline" href={viewModel.liveViewUrl}>
            {viewModel.liveViewUrl}
          </a>
        </div>
        <div className="mt-1 italic">
          Detail (step breakdown, screenshots, raw logs) is in OttoTester.
        </div>
      </footer>
    </div>
  )
}

// ── Internal components ─────────────────────────────────────────────

/** Plain-language result label for an affected test in the Issues
 * section (#1515 Slice 4). Failing statuses collapse to "failed". */
function issueResultLabel(
  status: 'passed' | 'failed' | 'skipped' | 'timedOut' | 'interrupted',
): string {
  if (status === 'passed') return '✓ passed'
  if (status === 'skipped') return 'skipped'
  return '✗ failed'
}
function issueResultTone(
  status: 'passed' | 'failed' | 'skipped' | 'timedOut' | 'interrupted',
): string {
  if (status === 'passed') return 'text-green-700'
  if (status === 'skipped') return 'text-gray-500'
  return 'text-red-700'
}

function SummaryTile({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: number
  tone?: 'neutral' | 'success' | 'destructive' | 'muted'
}) {
  return (
    <div className="rounded border border-gray-200 p-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div
        className={cn(
          'mt-1 text-xl font-bold tabular-nums leading-tight',
          tone === 'success' && value > 0 && 'text-green-700',
          tone === 'destructive' && value > 0 && 'text-red-700',
          tone === 'muted' && 'text-gray-500',
        )}
      >
        {value}
      </div>
    </div>
  )
}

function RunTypeBadge({ runType }: { runType: 'discovery' | 'replay' }) {
  const cls =
    runType === 'replay'
      ? 'border-green-300 bg-green-50 text-green-800'
      : 'border-amber-300 bg-amber-50 text-amber-800'
  return (
    <span
      className={cn(
        'inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase',
        cls,
      )}
    >
      {runType}
    </span>
  )
}

function StatusBadge({
  status,
}: {
  status: RunDetailViewModel['context']['status']
}) {
  const tone =
    status === 'completed'
      ? 'border-green-300 bg-green-50 text-green-800'
      : status === 'failed'
      ? 'border-red-300 bg-red-50 text-red-800'
      : status === 'cancelled'
      ? 'border-gray-300 bg-gray-50 text-gray-700'
      : 'border-amber-300 bg-amber-50 text-amber-800'
  return (
    <span
      className={cn(
        'inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase',
        tone,
      )}
    >
      {status}
    </span>
  )
}

function DefectFigure({
  count,
  label,
  tone,
}: {
  count: number
  label: string
  tone: 'destructive' | 'warning' | 'muted'
}) {
  const dotClass =
    tone === 'destructive'
      ? 'bg-red-600'
      : tone === 'warning'
      ? 'bg-amber-500'
      : 'bg-gray-400'
  const countClass =
    count > 0 && tone === 'destructive'
      ? 'text-red-700'
      : count > 0 && tone === 'warning'
      ? 'text-amber-700'
      : ''
  return (
    <div className="flex items-center gap-1.5">
      <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-sm', dotClass)} />
      <span className={cn('text-base font-bold tabular-nums leading-none', countClass)}>
        {count}
      </span>
      <span className="text-xs text-gray-600">{label}</span>
    </div>
  )
}

function RegressionSection({
  runType,
  regression,
}: {
  runType: 'discovery' | 'replay'
  regression: RdRegressionSummary | null
}) {
  if (runType === 'discovery') {
    return (
      <div className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-600">
        Regression delta isn't applicable to discovery runs — the planner
        emits a different test set each run, so cross-run classification
        is ill-defined.
      </div>
    )
  }
  if (!regression) {
    return (
      <div className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-600">
        Couldn't compute a regression delta for this run.
      </div>
    )
  }
  if (regression.priorRunId === null) {
    return (
      <div className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-600">
        First run on this variant — no prior run to compare to.
      </div>
    )
  }
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-gray-600">
        vs prior run{' '}
        <code className="font-mono text-gray-700">
          {regression.priorRunId.slice(0, 8)}…
        </code>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <RegressionTile
          label="New failures"
          value={regression.newlyFailing}
          tone="destructive"
        />
        <RegressionTile
          label="New passes"
          value={regression.newlyPassing}
          tone="success"
        />
        <RegressionTile
          label="Persistent failures"
          value={regression.persistentlyFailing}
          tone="destructive"
        />
        <RegressionTile label="Flipped" value={regression.flipped} />
      </div>
    </div>
  )
}

function RegressionTile({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'success' | 'destructive'
}) {
  return (
    <div className="rounded border border-gray-200 p-2.5">
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div
        className={cn(
          'mt-0.5 text-lg font-bold tabular-nums leading-tight',
          tone === 'success' && value > 0 && 'text-green-700',
          tone === 'destructive' && value > 0 && 'text-red-700',
        )}
      >
        {value}
      </div>
    </div>
  )
}

